// Accès aux données pour l'espace organisateur. Les droits sont vérifiés par Supabase (RLS) :
// le site ne peut rien faire de plus que l'app.

import { routeLength, simplify, type LatLng } from '@shared/geo';
import { isSameTrack, signatureOf } from '@shared/duplicates';
import type { Maneuver } from '@shared/maneuvers';
import type { TripResult } from '@shared/eventRanking';
import { ALERT_VISIBLE_MS, type RoadAlert } from '@shared/roadAlerts';
import { type DisplayLine, displayLinePoints } from '@shared/osrmMatch';
import type { SpeedLimitSpan } from '@shared/speedLimits';
import type {
  EventKind,
  EventRow,
  LivePosition,
  Participant,
  ParticipationStatus,
  PrivateInfo,
  Profile,
  Role,
} from '@shared/types';
import { db } from './supabase';

type DbPoints = [number, number][];
export type RouteSummary = {
  id: string;
  name: string;
  distance_m: number;
  source: string;
  created_at: string;
  maneuvers_source: string | null;
};
export type RouteRow = RouteSummary & {
  points: DbPoints;
  maneuvers: Maneuver[] | null;
  speed_limits?: SpeedLimitSpan[] | null;
  display_line?: DisplayLine | null;
};
export type { EventKind, EventRow, LivePosition, Participant, PrivateInfo, Profile, Role };

const check = <T>({ data, error }: { data: T | null; error: { message: string } | null }): T => {
  if (error) throw new Error(error.message);
  return data as T;
};

export const toPoints = (p: DbPoints): LatLng[] => p.map(([latitude, longitude]) => ({ latitude, longitude }));
const toDb = (p: LatLng[]): DbPoints => p.map((x) => [x.latitude, x.longitude]);

// ---------- Profil ----------
export async function getProfile(id: string): Promise<Profile | null> {
  const { data } = await db().from('profiles').select('id, display_name, car, role, approved').eq('id', id).single();
  return (data as Profile) ?? null;
}

export async function listProfiles(): Promise<Profile[]> {
  // toutes les colonnes : fonctionne avant et après la migration 0026 (email_confirmed)
  return check(await db().from('profiles').select('*').order('display_name'));
}

export async function listPrivateInfo(): Promise<Map<string, PrivateInfo>> {
  const rows = check(await db().from('profile_private').select('id, first_name, last_name, full_name, license_plate'));
  return new Map((rows as (PrivateInfo & { id: string })[]).map(({ id, ...info }) => [id, info]));
}

/** Staff : invitations envoyées (validées automatiquement à l'inscription, migration 0030). */
export type Invitation = { email: string; created_at: string; accepted_at: string | null };

export async function listInvitations(): Promise<Invitation[]> {
  return check(
    await db().from('invitations').select('email, created_at, accepted_at').order('created_at', { ascending: false }),
  ) as Invitation[];
}

/** Enregistre l'invitation (ou la remet à la date du jour si elle existe déjà). */
export async function inviteMember(email: string, userId: string): Promise<void> {
  check(
    await db()
      .from('invitations')
      .upsert({ email, invited_by: userId, created_at: new Date().toISOString() }, { onConflict: 'email' }),
  );
}

export async function deleteInvitation(email: string): Promise<void> {
  check(await db().from('invitations').delete().eq('email', email));
}

export async function listMemberEmails(): Promise<Map<string, string>> {
  const rows = check(await db().rpc('member_emails')) as { id: string; email: string | null }[] | null;
  return new Map((rows ?? []).filter((r) => r.email).map((r) => [r.id, r.email!]));
}

export async function setApproved(id: string, approved: boolean) {
  check(await db().from('profiles').update({ approved }).eq('id', id));
}

/** « Retirer l'accès » : supprime le compte d'un membre et toutes ses données (staff / admin). */
export async function removeMember(id: string) {
  const photos = (check(await db().rpc('remove_member', { p_id: id })) as string[] | null) ?? [];
  // photos de ses messages : les fichiers ne partent pas avec la cascade SQL
  for (let i = 0; i < photos.length; i += 100) {
    await db()
      .storage.from('chat')
      .remove(photos.slice(i, i + 100))
      .catch(() => {});
  }
}

export async function setRole(id: string, role: Role) {
  check(await db().from('profiles').update({ role }).eq('id', id));
}

// ---------- Événements ----------
export type EventInput = Omit<EventRow, 'id' | 'interested_count'>;

export async function listEvents(): Promise<EventRow[]> {
  return check(await db().from('events_with_counts').select('*').order('event_date', { ascending: false }));
}

export async function getEvent(id: string): Promise<EventRow> {
  return check(await db().from('events_with_counts').select('*').eq('id', id).single());
}

/** Nouvel événement : brouillon (visible du staff seulement) jusqu'à « Publier ». */
export async function createEvent(e: EventInput): Promise<string> {
  const row = check(
    await db()
      .from('events')
      .insert({ ...e, published: false })
      .select('id')
      .single(),
  ) as {
    id: string;
  };
  return row.id;
}

/** Publie un brouillon : visible par tous les membres, qui sont prévenus par notification. */
export async function publishEvent(id: string) {
  check(await db().from('events').update({ published: true }).eq('id', id));
}

/** Remet un événement en brouillon : il disparaît pour les membres (pas de notification). */
export async function unpublishEvent(id: string) {
  check(await db().from('events').update({ published: false }).eq('id', id));
}

export async function updateEvent(id: string, e: Partial<EventInput>) {
  check(await db().from('events').update(e).eq('id', id));
}

export async function deleteEvent(id: string) {
  check(await db().from('events').delete().eq('id', id));
}

export async function listParticipants(eventId: string): Promise<Participant[]> {
  const rows = check(
    await db()
      .from('event_interests')
      .select('status, profiles(id, display_name, car, role, approved)')
      .eq('event_id', eventId),
  ) as unknown as { status: ParticipationStatus; profiles: Profile | null }[];
  return rows.filter((r) => r.profiles).map((r) => ({ ...r.profiles!, status: r.status }));
}

// ---------- Résultats des participants (classements de la fiche) ----------
export async function listEventResults(eventId: string): Promise<TripResult[]> {
  return check(
    await db()
      .from('trips')
      .select('user_id, distance_m, max_speed_kmh, avg_speed_kmh, completed_pct')
      .eq('event_id', eventId),
  ) as TripResult[];
}

// ---------- Signalements (dangers, SOS) et annonces ----------
export async function listRoadAlerts(eventId: string): Promise<RoadAlert[]> {
  const since = new Date(Date.now() - ALERT_VISIBLE_MS).toISOString();
  return check(
    await db()
      .from('road_alerts')
      .select('id, event_id, user_id, kind, lat, lon, created_at, resolved_at')
      .eq('event_id', eventId)
      .gte('created_at', since)
      .order('created_at', { ascending: false }),
  ) as RoadAlert[];
}

export async function resolveRoadAlert(id: string): Promise<void> {
  check(await db().from('road_alerts').update({ resolved_at: new Date().toISOString() }).eq('id', id));
}

export function subscribeRoadAlerts(eventId: string, onChange: (a: RoadAlert, deleted: boolean) => void) {
  const channel = db()
    .channel(`alerts:${eventId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'road_alerts', filter: `event_id=eq.${eventId}` },
      (payload) => {
        if (payload.eventType === 'DELETE') onChange(payload.old as RoadAlert, true);
        else onChange(payload.new as RoadAlert, false);
      },
    )
    .subscribe();
  return () => {
    db().removeChannel(channel);
  };
}

/** Annonce du staff : message de la discussion lu à la voix dans les voitures en route. */
export async function sendAnnouncement(eventId: string, body: string): Promise<void> {
  check(await db().from('chat_messages').insert({ event_id: eventId, body, announcement: true }));
}

// ---------- Positions en direct ----------
/** Remet « Inscrit » les participants restés « Actif » sans position depuis 30 min (aussi fait par la base). */
export async function expireStaleActivity(): Promise<void> {
  check(await db().rpc('expire_stale_activity'));
}

export async function listPositions(eventId: string): Promise<LivePosition[]> {
  return check(await db().from('live_positions').select('*').eq('event_id', eventId));
}

/** Positions des participants en temps réel ; renvoie la fonction d'arrêt. */
export function subscribePositions(eventId: string, onChange: (p: LivePosition, deleted: boolean) => void) {
  const channel = db()
    .channel(`positions:${eventId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'live_positions', filter: `event_id=eq.${eventId}` },
      (payload) => {
        if (payload.eventType === 'DELETE') onChange(payload.old as LivePosition, true);
        else onChange(payload.new as LivePosition, false);
      },
    )
    .subscribe();
  return () => {
    db().removeChannel(channel);
  };
}

// ---------- Tracés ----------
export async function listRoutes(): Promise<RouteSummary[]> {
  return check(
    await db()
      .from('routes')
      .select('id, name, distance_m, source, created_at, maneuvers_source')
      .order('created_at', { ascending: false }),
  );
}

export async function getRoute(id: string): Promise<RouteRow> {
  return check(await db().from('routes').select('*').eq('id', id).single());
}

/** Nom du tracé déjà enregistré identique (même départ, arrivée et longueur), sinon null. */
export async function findDuplicateRoute(points: LatLng[]): Promise<string | null> {
  const simplified = simplify(points, 3);
  const sig = signatureOf(simplified, routeLength(simplified));
  const margin = Math.max(20, sig.distance * 0.005);
  const rows = check(
    await db()
      .from('routes')
      .select('name, points, distance_m')
      .gte('distance_m', sig.distance - margin)
      .lte('distance_m', sig.distance + margin),
  ) as { name: string; points: DbPoints; distance_m: number }[];
  return (
    rows.find((r) => r.points.length > 0 && isSameTrack(sig, signatureOf(toPoints(r.points), r.distance_m)))?.name ??
    null
  );
}

export async function saveRoute(name: string, points: LatLng[]): Promise<{ id: string; points: LatLng[] }> {
  const simplified = simplify(points, 3);
  const row = check(
    await db()
      .from('routes')
      .insert({ name, points: toDb(simplified), distance_m: routeLength(simplified), source: 'import' })
      .select('id')
      .single(),
  ) as { id: string };
  return { id: row.id, points: simplified };
}

export async function renameRoute(id: string, name: string) {
  check(await db().from('routes').update({ name }).eq('id', id));
}

export async function deleteRoute(id: string) {
  check(await db().from('routes').delete().eq('id', id));
}

export async function setRouteDisplayLine(id: string, line: DisplayLine) {
  check(await db().from('routes').update({ display_line: line }).eq('id', id));
}

/** Points à dessiner pour un tracé : recalés sur les routes s'ils existent, sinon le tracé brut. */
export function routeDrawing(r: { points: DbPoints; display_line?: DisplayLine | null }): LatLng[] {
  return r.display_line?.length ? displayLinePoints(r.display_line).points : toPoints(r.points);
}

export async function setRouteSpeedLimits(id: string, spans: SpeedLimitSpan[]) {
  check(await db().from('routes').update({ speed_limits: spans }).eq('id', id));
}

export async function setRouteManeuvers(id: string, maneuvers: Maneuver[], source: string) {
  check(await db().from('routes').update({ maneuvers, maneuvers_source: source }).eq('id', id));
}
